import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { FormPageNameFieldSkeleton } from "@/components/forms/FormPageNameField";

export type KvRowWidth = {
    labelWidth: string;
    valueWidth?: string;
    valueMaxWidth?: string;
    valueKind?: "text" | "badge";
    badgeWidth?: string;
};

export const DEFAULT_TAB_LIST_ROW_WIDTHS = ["14rem", "11rem", "16rem", "10rem", "13rem", "9rem"] as const;
export const DEFAULT_TABLE_ROW_COUNT = 8;
export const ROLE_DETAIL_TAB_WIDTHS = ["6.5rem", "3.5rem", "4.5rem"] as const;
export const GROUP_DETAIL_TAB_WIDTHS = ["3.5rem", "3.5rem"] as const;
export const DEFAULT_FORM_TOGGLE_ROW_COUNT = 20;

export function SkeletonBar({
    className = "h-4 w-full",
    style,
}: {
    className?: string;
    style?: React.CSSProperties;
}) {
    return <Skeleton className={className} style={style} />;
}

export function MobileDateRowSkeleton({ labelWidth = "4.875rem" }: { labelWidth?: string }) {
    return (
        <div className="text-dark gs-mobile-date-field">
            <Skeleton
                className="gs-list-table-skeleton-placeholder gs-mobile-skeleton-date-label shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <div className="gs-mobile-date-values">
                <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date" />
                <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date-sub" />
            </div>
        </div>
    );
}

export function MobileLabelRowSkeleton({
    labelWidth = "4rem",
    valueWidth = "7rem",
    valueKind = "text",
}: {
    labelWidth?: string;
    valueWidth?: string;
    valueKind?: "text" | "count";
}) {
    return (
        <div className="text-dark gs-mobile-field-row">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            {valueKind === "count" ? (
                <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
            ) : (
                <Skeleton
                    className="gs-list-table-skeleton-placeholder shrink-0"
                    style={{ width: valueWidth, maxWidth: "100%", height: "0.8125rem", minHeight: "0.8125rem" }}
                />
            )}
        </div>
    );
}

/** Template mobile stats — single inline row like live (label + count groups with ms-2). */
export function MobileTemplateStatsSkeleton() {
    const bar = (width: string, extraClass = "") => (
        <Skeleton
            className={`gs-list-table-skeleton-placeholder shrink-0 ${extraClass}`.trim()}
            style={{ width, height: "0.8125rem", minHeight: "0.8125rem" }}
        />
    );

    return (
        <div className="text-dark gs-template-mobile-skeleton-stats">
            {bar("6.5rem")}
            <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
            {bar("4.25rem", "gs-template-mobile-skeleton-stat-gap")}
            <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
            {bar("3.75rem", "gs-template-mobile-skeleton-stat-gap")}
            <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--count" />
        </div>
    );
}

/** First line title/subject — matches live card content line-height (1.5rem). */
export function MobileCardTitleSkeleton({
    variant = "envelope",
}: {
    variant?: "envelope" | "form" | "template";
}) {
    const className =
        variant === "envelope"
            ? "gs-envelope-mobile-skeleton-subject"
            : "gs-template-mobile-skeleton-title";

    return (
        <div className="gs-mobile-skeleton-text-line">
            <Skeleton className={`${className} gs-list-table-skeleton-placeholder`} />
        </div>
    );
}

/** One content line in envelope/form/template mobile skeleton — matches 1.5rem line-height. */
export function MobileEnvelopeSkeletonLine({ children }: { children: React.ReactNode }) {
    return <div className="gs-envelope-mobile-skeleton-line">{children}</div>;
}

/** Envelope mobile "To: recipient" row inside `.text-soft`. */
export function MobileEnvelopeRecipientSkeleton() {
    return (
        <div className="text-soft gs-envelope-mobile-skeleton-recipient">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "1.375rem", height: "0.6875rem", minHeight: "0.6875rem" }}
            />
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "5.5rem", maxWidth: "70%", height: "0.6875rem", minHeight: "0.6875rem" }}
            />
        </div>
    );
}

/** Envelope / form mobile status — label + badge (matches typical StatusBadge height). */
export function MobileEnvelopeStatusSkeleton({
    labelWidth = "3.5rem",
    badgeWidth = "4.5rem",
}: {
    labelWidth?: string;
    badgeWidth?: string;
}) {
    return (
        <div className="text-dark gs-envelope-mobile-status-field">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <span className="gs-envelope-mobile-status-value">
                <MobileStatusBadgeSkeleton width={badgeWidth} />
            </span>
        </div>
    );
}

/** Label row with badge value (form status, reported-user status). */
export function MobileLabelBadgeRowSkeleton({
    labelWidth = "3.25rem",
    badgeWidth = "4.5rem",
}: {
    labelWidth?: string;
    badgeWidth?: string;
}) {
    return (
        <div className="text-dark gs-mobile-field-row">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <span className="gs-mobile-field-value">
                <MobileStatusBadgeSkeleton width={badgeWidth} />
            </span>
        </div>
    );
}

/** Reported-user mobile email + name block. */
export function MobileUserInfoSkeleton() {
    return (
        <div className="user-info">
            <Skeleton className="gs-reported-user-skeleton-email gs-list-table-skeleton-placeholder" />
            <Skeleton className="gs-reported-user-skeleton-name gs-list-table-skeleton-placeholder" />
        </div>
    );
}

/** Tag mobile list row — matches TagTable gs-mobile-list-card layout. */
export function TagMobileRowSkeleton({
    tableId,
    index,
}: {
    tableId: string;
    index: number;
}) {
    return (
        <div
            className="envelope_tr gs-mobile-list-card gs-users-table-skeleton-mobile"
            id={tableSkeletonLoaderId(tableId, index, true)}
        >
            <div className="gs-mobile-card-icon">
                <Skeleton
                    className="gs-list-table-skeleton-icon gs-list-table-skeleton-icon--tag"
                    aria-hidden
                />
            </div>
            <div className="env_meta_data gs-mobile-list-card__content w-100">
                <MobileTextDarkRowSkeleton labelWidth="2.75rem" valueWidth="6rem" />
                <MobileLabelRowSkeleton labelWidth="6.25rem" valueKind="count" />
                <MobileLabelRowSkeleton labelWidth="3.5rem" valueWidth="7rem" />
                <MobileDateRowSkeleton labelWidth="5.5rem" />
                <div className="text-dark pt-1">
                    <MobileActionButtonSkeleton width="5.5rem" />
                </div>
            </div>
        </div>
    );
}

/** Envelope mobile sent status — progress layout for sent envelopes only. */
export function MobileEnvelopeSentStatusSkeleton() {
    return (
        <div className="text-dark gs-envelope-mobile-status-field">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "3rem", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <div className="gs-envelope-mobile-status-value">
                <div className="project-list-progress p-0 gs-envelope-mobile-skeleton-progress">
                    <Skeleton className="gs-list-table-skeleton-placeholder gs-envelope-mobile-skeleton-progress-track" />
                    <Skeleton className="gs-list-table-skeleton-placeholder gs-envelope-mobile-skeleton-done" />
                </div>
                <div className="meta mt-1">
                    <Skeleton className="gs-list-table-skeleton-placeholder gs-envelope-mobile-skeleton-status-label" />
                </div>
            </div>
        </div>
    );
}

/** Inline label + value inside live `.text-dark` row (Sent by, Owner, tag fields). */
export function MobileTextDarkRowSkeleton({
    labelWidth = "4rem",
    valueWidth = "7rem",
    inline = false,
}: {
    labelWidth?: string;
    valueWidth?: string;
    /** Use outside `.envelope_table` where text-dark flex rules do not apply (e.g. tag list). */
    inline?: boolean;
}) {
    return (
        <div className={inline ? "text-dark gs-mobile-inline-field-skeleton" : "text-dark"}>
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: valueWidth, maxWidth: "100%", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
        </div>
    );
}

export function MobileLogsLineSkeleton({
    labelWidth = "4.5rem",
    valueWidth = "10rem",
    valueMaxWidth = "100%",
    spaced = false,
}: {
    labelWidth?: string;
    valueWidth?: string;
    valueMaxWidth?: string;
    spaced?: boolean;
}) {
    return (
        <div
            className={`text-dark gs-logs-mobile__line${spaced ? " gs-logs-mobile__line--spaced" : ""}`}
        >
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: labelWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <Skeleton
                className="gs-list-table-skeleton-placeholder gs-logs-mobile__inline-value min-w-0"
                style={{ width: valueWidth, maxWidth: valueMaxWidth, height: "0.8125rem", minHeight: "0.8125rem" }}
            />
        </div>
    );
}

/** Action By — label + name + email on one line (matches live mobile log row). */
export function MobileLogsActionBySkeleton() {
    return (
        <div className="text-dark gs-logs-mobile__line">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "4.5rem", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "5.5rem", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <Skeleton
                className="gs-list-table-skeleton-placeholder gs-logs-mobile-skeleton-email shrink-0"
                style={{ width: "7.5rem", maxWidth: "45%", height: "0.6875rem", minHeight: "0.6875rem" }}
            />
        </div>
    );
}

export function MobileLogsCategoryRowSkeleton() {
    return (
        <div className="text-dark gs-logs-mobile__line gs-logs-mobile__line--spaced">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "4rem", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <MobileStatusBadgeSkeleton width="2.75rem" />
        </div>
    );
}

export function LogsMobileRowSkeleton({
    tableId,
    index,
    children,
}: {
    tableId: string;
    index: number;
    children: React.ReactNode;
}) {
    return (
        <div
            className="envelope_tr gs-logs-mobile__row gs-users-table-skeleton-mobile"
            id={tableSkeletonLoaderId(tableId, index, true)}
        >
            <div className="env_meta_data gs-logs-mobile__body w-100">{children}</div>
        </div>
    );
}

/** Left-aligned mobile action placeholder (resend, tag actions, reported-user menu). */
export function MobileActionButtonSkeleton({ width = "5.75rem" }: { width?: string }) {
    return (
        <Skeleton
            className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-mobile-action-btn"
            style={{ width, height: "calc(2.125rem + 2px)", minHeight: "calc(2.125rem + 2px)" }}
        />
    );
}

export function MobileListChevronSkeleton() {
    return (
        <span className="gs-mobile-list-chevron gs-mobile-list-chevron-skeleton" aria-hidden>
            <Skeleton className="gs-mobile-list-chevron-skeleton-icon rounded-sm" />
        </span>
    );
}

export function DesktopDateStackSkeleton({
    dateWidth = "5.5rem",
    timeWidth = "4rem",
}: {
    dateWidth?: string;
    timeWidth?: string;
}) {
    return (
        <>
            <Skeleton className="h-4" style={{ width: dateWidth }} />
            <Skeleton className="gs-table-subject-skeleton-sub h-3" style={{ width: timeWidth }} />
        </>
    );
}

/** List-table date column — matches `.gs-users-table-date` + `.meta.mt-1` time row. */
export function DesktopPlaceholderDateStackSkeleton() {
    return (
        <>
            <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date" />
            <div className="meta mt-1">
                <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--date-sub" />
            </div>
        </>
    );
}

export function DesktopIconTitleColumnSkeleton({
    iconClass,
    titleClass,
    wrapTitleInInfo = false,
}: {
    iconClass: string;
    titleClass: string;
    wrapTitleInInfo?: boolean;
}) {
    const title = <Skeleton className={titleClass} />;

    return (
        <div className="project-title align-middle d-flex align-items-center">
            <div className="icon mr-2 fs-5 mt-1">
                <Skeleton className={iconClass} aria-hidden />
            </div>
            {wrapTitleInInfo ? <div className="info min-w-0">{title}</div> : title}
        </div>
    );
}

export function DesktopSubjectIconTwoLinesSkeleton({
    wrapperClassName = "nk-tnx-type",
    iconClass = "h-8 w-8 shrink-0 rounded mr-2",
    titleWidth = "200px",
    subWidth = "140px",
}: {
    wrapperClassName?: string;
    iconClass?: string;
    titleWidth?: string;
    subWidth?: string;
}) {
    return (
        <div className={wrapperClassName} style={{ display: "flex", alignItems: "center" }}>
            <Skeleton className={iconClass} aria-hidden />
            <div style={{ minWidth: 0, flex: 1 }}>
                <Skeleton className="h-4 max-w-full" style={{ width: titleWidth }} />
                <Skeleton className="gs-table-subject-skeleton-sub h-3 max-w-full" style={{ width: subWidth }} />
            </div>
        </div>
    );
}

export function DesktopStatusBadgeSkeleton({ width = "70px" }: { width?: string }) {
    return <Skeleton className="h-6 rounded-full" style={{ width }} />;
}

export function MobileStatusBadgeSkeleton({ width = "3.5rem" }: { width?: string }) {
    return (
        <Skeleton
            className="gs-list-table-skeleton-placeholder h-6 rounded-full"
            style={{ width }}
        />
    );
}

/** Generic compact action pill (e.g. Users "Action" dropdown) — not transfer ownership. */
export function DesktopTransferActionSkeleton() {
    return <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--btn" />;
}

/** Matches TransferOwnershipButton size in table action columns. */
export function DesktopTransferOwnershipSkeleton() {
    return (
        <Skeleton className="gs-list-table-skeleton-placeholder gs-transfer-ownership-btn-skeleton gs-transfer-ownership-btn-skeleton--table" />
    );
}

/** Mobile transfer button under date fields — matches stacked TransferOwnershipButton. */
export function MobileTransferOwnershipSkeleton() {
    return (
        <div className="gs-mobile-transfer-ownership">
            <Skeleton className="gs-list-table-skeleton-placeholder gs-transfer-ownership-btn-skeleton gs-transfer-ownership-btn-skeleton--mobile" />
        </div>
    );
}

export function DesktopIconTitleCellSkeleton({
    titlePlaceholderClass = "gs-list-table-skeleton-placeholder--email",
    iconClass = "gs-list-table-skeleton-icon",
}: {
    titlePlaceholderClass?: string;
    iconClass?: string;
}) {
    return (
        <div className="user-card gs-users-table-user-cell">
            <div className="gs-users-table-user-icon">
                <Skeleton className={iconClass} aria-hidden />
            </div>
            <div className="gs-users-table-user-info">
                <Skeleton className={`gs-list-table-skeleton-placeholder ${titlePlaceholderClass}`} />
            </div>
        </div>
    );
}

export function MobileUserStatusRowSkeleton() {
    return (
        <div className="text-dark gs-users-mobile-status">
            <Skeleton
                className="gs-list-table-skeleton-placeholder shrink-0"
                style={{ width: "3.25rem", height: "0.8125rem", minHeight: "0.8125rem" }}
            />
            <Skeleton className="gs-list-table-skeleton-placeholder gs-list-table-skeleton-placeholder--switch" />
        </div>
    );
}

export function MobileEnvelopeStyleRowSkeleton({
    tableId,
    index,
    children,
}: {
    tableId: string;
    index: number;
    children: React.ReactNode;
}) {
    return (
        <div
            className="envelope_tr gs-mobile-list-card gs-users-table-skeleton-mobile"
            id={tableSkeletonLoaderId(tableId, index, true)}
        >
            <div className="gs-mobile-card-icon">
                <Skeleton className="gs-list-table-skeleton-icon" aria-hidden />
            </div>
            <div className="env_meta_data gs-mobile-list-card__content w-100">{children}</div>
            <MobileListChevronSkeleton />
        </div>
    );
}

export function tableSkeletonLoaderId(tableId: string, index: number, mobile = false) {
    if (index !== 0) return undefined;
    return mobile ? `${tableId}-table-data-loader-mb` : `${tableId}-table-data-loader`;
}

export function mapSkeletonRows(rowCount: number, renderRow: (index: number) => ReactNode) {
    return Array.from({ length: rowCount }, (_, index) => renderRow(index));
}

/** Mobile list card — icon + meta + optional chevron (users, roles, groups, invite). */
export function MobileListCardSkeleton({
    tableId,
    index,
    showChevron = true,
    cardClassName,
    metaClassName = "gs-users-mobile-meta",
    children,
}: {
    tableId: string;
    index: number;
    showChevron?: boolean;
    /** Pass `gs-mobile-list-card` when the live row uses it (users/roles/groups). Omit for invite. */
    cardClassName?: string;
    metaClassName?: string;
    children: ReactNode;
}) {
    const classes = ["gs-users-mobile-card", cardClassName, "gs-users-table-skeleton-mobile"]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={classes} id={tableSkeletonLoaderId(tableId, index, true)}>
            <div className="gs-mobile-card-icon">
                <Skeleton className="gs-list-table-skeleton-icon" aria-hidden />
            </div>
            <div className={metaClassName}>{children}</div>
            {showChevron ? <MobileListChevronSkeleton /> : null}
        </div>
    );
}

export function MobileEnvelopeListSkeleton({ children }: { children: React.ReactNode }) {
    return <div className="envelope_table">{children}</div>;
}

export function MobileTagListSkeleton({ children }: { children: React.ReactNode }) {
    return <div className="tag_table">{children}</div>;
}

export function LinesStackSkeleton({
    rowCount = 4,
    wrapperClassName,
    rowClassName = "h-10 w-full",
    className,
}: {
    rowCount?: number;
    wrapperClassName?: string;
    rowClassName?: string;
    /** @deprecated use wrapperClassName */
    className?: string;
}) {
    return (
        <div className={wrapperClassName ?? className ?? "space-y-3"}>
            {Array.from({ length: rowCount }).map((_, index) => (
                <Skeleton key={index} className={rowClassName} />
            ))}
        </div>
    );
}

export function DetailListRowSkeleton({
    as: Tag = "li",
    className = "flex items-center px-5 py-3",
    barClassName = "h-4 w-full",
}: {
    as?: "li" | "div";
    className?: string;
    barClassName?: string;
}) {
    return (
        <Tag className={className}>
            <Skeleton className={barClassName} />
        </Tag>
    );
}

export function ToggleListSkeleton({
    rowCount = 20,
    labelWidth,
}: {
    rowCount?: number;
    labelWidth?: string;
}) {
    return (
        <>
            {Array.from({ length: rowCount }).map((_, index) => (
                <li key={index} className="gs-detail-list-item">
                    <div className="gs-status-toggle gs-toggle-list-skeleton-row" aria-hidden>
                        <Skeleton className="gs-toggle-list-skeleton-switch" />
                        <Skeleton
                            className="gs-toggle-list-skeleton-label"
                            style={labelWidth ? { width: labelWidth } : undefined}
                        />
                    </div>
                </li>
            ))}
        </>
    );
}

export function TabListSkeleton({ rowCount = 6, id }: { rowCount?: number; id?: string }) {
    return (
        <div className="gs-list-page-card gs-tab-list-skeleton">
            <ul className="gs-detail-list">
                {Array.from({ length: rowCount }).map((_, index) => (
                    <li
                        className="gs-tab-list-skeleton-row"
                        key={index}
                        id={index === 0 && id ? id : undefined}
                    >
                        <Skeleton
                            className="gs-tab-list-skeleton-bar gs-tab-list-skeleton-bar--grow"
                            style={{ width: "10rem" }}
                        />
                        <Skeleton className="gs-tab-list-skeleton-bar gs-tab-list-skeleton-bar--icon" />
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function SettingKeyValueRowsSkeleton({
    id,
    rowCount = 3,
    labelClassName = "h-4 w-40",
    valueClassName = "h-4 w-56",
}: {
    id?: string;
    rowCount?: number;
    labelClassName?: string;
    valueClassName?: string;
}) {
    return (
        <div id={id}>
            {Array.from({ length: rowCount }).map((_, index) => (
                <div key={index} className="gs-setting-skeleton-row">
                    <Skeleton className={labelClassName} />
                    <Skeleton className={valueClassName} />
                </div>
            ))}
        </div>
    );
}

export function DocumentModalSkeleton({ bare = false }: { bare?: boolean }) {
    const body = (
        <div className="gs-document-dialog__body">
            <div className="space-y-3">
                <Skeleton className="h-[28rem] w-full rounded-md" />
            </div>
        </div>
    );

    if (bare) {
        return body;
    }

    return (
        <>
            <div className="gs-document-dialog__header">
                <Skeleton className="h-5 w-2/3 max-w-xs" />
            </div>
            {body}
        </>
    );
}

export function DocumentListItemSkeleton() {
    return (
        <li className="space-y-2 px-5 py-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
        </li>
    );
}

export function DetailHeaderSkeleton({
    titleClassName = "gs-envelope-detail-skeleton-title",
    actionClassName = "gs-transfer-ownership-btn-skeleton gs-transfer-ownership-btn-skeleton--detail",
    showAction = true,
    wrapperClassName = "gs-detail-header",
    bare = false,
}: {
    titleClassName?: string;
    actionClassName?: string;
    showAction?: boolean;
    wrapperClassName?: string;
    /** Render title/action only — for title-only headers wrapped by the page. */
    bare?: boolean;
}) {
    const title = <Skeleton className={titleClassName} />;
    const action = showAction ? <Skeleton className={actionClassName} /> : null;

    if (bare) {
        return (
            <>
                {title}
                {action}
            </>
        );
    }

    return (
        <div className={wrapperClassName}>
            {title}
            {action}
        </div>
    );
}

export function DetailTabsSkeleton({
    tabWidths,
    className = "gs-page-tabs gs-role-detail-skeleton-tabs",
}: {
    tabWidths: readonly string[];
    className?: string;
}) {
    return (
        <ul className={className}>
            {tabWidths.map((width, index) => (
                <li key={`${width}-${index}`}>
                    <Skeleton
                        className={`gs-role-detail-skeleton-tab${index === 0 ? " gs-role-detail-skeleton-tab--active" : ""}`}
                        style={{ width }}
                    />
                </li>
            ))}
        </ul>
    );
}

export function DetailKvRowsSkeleton({
    rows,
    cardClassName = "gs-list-page-card gs-envelope-detail-card",
    rowClassName = "gs-envelope-detail-kv-row gs-envelope-detail-skeleton-row",
    innerClassName = "gs-envelope-detail-kv-inner gs-envelope-detail-skeleton-row-inner",
    labelClassName = "gs-envelope-detail-kv-label gs-envelope-detail-skeleton-label",
    valueClassName = "gs-envelope-detail-kv-value gs-envelope-detail-skeleton-value",
    barClassName = "gs-envelope-detail-skeleton-bar",
}: {
    rows: readonly KvRowWidth[];
    cardClassName?: string;
    rowClassName?: string;
    innerClassName?: string;
    labelClassName?: string;
    valueClassName?: string;
    barClassName?: string;
}) {
    return (
        <div className={cardClassName}>
            <ul className="m-0 list-none p-0">
                {rows.map((row, index) => (
                    <li key={index} className={rowClassName}>
                        <div className={innerClassName}>
                            <div className={labelClassName}>
                                <Skeleton className={barClassName} style={{ width: row.labelWidth }} />
                            </div>
                            <div className={valueClassName}>
                                {row.valueKind === "badge" ? (
                                    <Skeleton
                                        className={`${barClassName} gs-envelope-detail-skeleton-badge`}
                                        style={{ width: row.badgeWidth ?? "4.5rem" }}
                                    />
                                ) : (
                                    <Skeleton
                                        className={barClassName}
                                        style={{
                                            width: row.valueWidth ?? "11rem",
                                            maxWidth: row.valueMaxWidth ?? "100%",
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function DetailSectionTitleSkeleton({
    className = "gs-envelope-detail-skeleton-section-title",
}: {
    className?: string;
}) {
    return (
        <h5 className="gs-detail-section-title">
            <Skeleton className={className} />
        </h5>
    );
}

export function DetailRecipientRowsSkeleton({
    rowCount = 1,
    cardClassName = "gs-list-page-card gs-envelope-detail-card",
}: {
    rowCount?: number;
    cardClassName?: string;
    barClassName?: string;
}) {
    return (
        <div className={cardClassName}>
            <ul className="data-list is-compact m-0 list-none p-0">
                {Array.from({ length: rowCount }).map((_, index) => (
                    <li key={index} className="data-item" style={{ display: "block" }}>
                        <div className="data-col gs-envelope-detail-recipient-col">
                            <div className="data-label label">
                                <div className="info gs-envelope-detail-recipient-info">
                                    <Skeleton
                                        className="gs-envelope-detail-recipient-bar gs-envelope-detail-recipient-bar--title"
                                        style={{ width: "8rem" }}
                                    />
                                    <Skeleton
                                        className="gs-envelope-detail-recipient-bar"
                                        style={{ width: "11rem" }}
                                    />
                                </div>
                            </div>
                            <div className="data-label label">
                                <span>
                                    <div className="info">
                                        <Skeleton
                                            className="gs-envelope-detail-recipient-bar gs-envelope-detail-recipient-bar--title"
                                            style={{ width: "7rem" }}
                                        />
                                    </div>
                                </span>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function DetailDocumentRowsSkeleton({
    rowCount = 3,
    cardClassName = "gs-list-page-card gs-envelope-detail-card",
    barClassName = "gs-envelope-detail-skeleton-bar",
}: {
    rowCount?: number;
    cardClassName?: string;
    barClassName?: string;
}) {
    return (
        <div className={cardClassName}>
            <ul className="m-0 list-none p-0">
                {Array.from({ length: rowCount }).map((_, index) => (
                    <li key={index} className="gs-envelope-detail-document-item">
                        <Skeleton className="gs-envelope-detail-document-icon" />
                        <Skeleton
                            className="gs-envelope-detail-skeleton-bar"
                            style={{ width: "14rem", maxWidth: "100%" }}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function TabbedDetailListSkeleton({
    tabWidths,
    rowCount = 20,
    listRowWidths = DEFAULT_TAB_LIST_ROW_WIDTHS,
    showAction = true,
    rootClassName = "gs-role-detail-skeleton",
}: {
    tabWidths: readonly string[];
    rowCount?: number;
    listRowWidths?: readonly string[];
    showAction?: boolean;
    rootClassName?: string;
}) {
    return (
        <div className={rootClassName} aria-hidden>
            <DetailHeaderSkeleton
                titleClassName="gs-role-detail-skeleton-title"
                actionClassName="gs-role-detail-skeleton-action"
                showAction={showAction}
            />
            <DetailTabsSkeleton tabWidths={tabWidths} />
            <div className="gs-list-page-card">
                <ul className="gs-detail-list">
                    {Array.from({ length: rowCount }).map((_, index) => (
                        <li key={index} className="gs-role-detail-skeleton-row">
                            <Skeleton
                                className="gs-role-detail-skeleton-bar"
                                style={{ width: listRowWidths[index % listRowWidths.length] }}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export function FormToggleListPageSkeleton({
    sectionTitle,
    toggleRowCount = DEFAULT_FORM_TOGGLE_ROW_COUNT,
    showNameField = true,
}: {
    sectionTitle: string;
    toggleRowCount?: number;
    showNameField?: boolean;
}) {
    return (
        <div className="gs-form-page gs-form-page-skeleton" aria-hidden>
            <div className="gs-form-page-header">
                <Skeleton className="gs-form-page-skeleton-title" />
                <div className="gs-form-page-actions flex gap-2">
                    <Skeleton className="gs-form-page-skeleton-btn" />
                    <Skeleton className="gs-form-page-skeleton-btn" />
                </div>
            </div>

            {showNameField ? <FormPageNameFieldSkeleton /> : null}

            <div className="gs-form-page-section">
                <h5 className="gs-form-section-title gs-form-section-title--spaced">{sectionTitle}</h5>
                <div className="gs-list-page-card">
                    <ul className="gs-detail-list gs-form-toggle-list">
                        <ToggleListSkeleton rowCount={toggleRowCount} />
                    </ul>
                </div>
            </div>
        </div>
    );
}

export function AssignmentListPageSkeleton({
    sectionTitle,
    rowCount = 5,
    showAction = true,
}: {
    sectionTitle: string;
    rowCount?: number;
    showAction?: boolean;
}) {
    return (
        <div className="gs-user-detail-skeleton w-full min-w-0" aria-hidden>
            <DetailHeaderSkeleton
                titleClassName="gs-user-detail-skeleton-title"
                actionClassName="gs-user-detail-skeleton-action"
                showAction={showAction}
            />
            <h5 className="gs-form-section-title gs-form-section-title--spaced">{sectionTitle}</h5>
            <div className="gs-list-page-card">
                <ul className="gs-detail-list gs-assignment-name-list">
                    {Array.from({ length: rowCount }).map((_, index) => (
                        <DetailListRowSkeleton
                            key={index}
                            className="gs-detail-list-item"
                            barClassName="h-4 w-40"
                        />
                    ))}
                </ul>
            </div>
        </div>
    );
}

/** User roles/groups edit — form page with toggle list only (no name field). */
export function UserAssignmentEditSkeleton({ sectionTitle }: { sectionTitle: string }) {
    return (
        <FormToggleListPageSkeleton sectionTitle={sectionTitle} showNameField={false} toggleRowCount={8} />
    );
}

export function ListPageHeaderSkeleton({ showFilter = true }: { showFilter?: boolean }) {
    return (
        <div className="gs-list-page-header" aria-hidden>
            <div className="gs-list-page-title-wrap">
                <Skeleton className="gs-list-page-header-skeleton-title" />
                <Skeleton className="gs-list-page-header-skeleton-count" />
            </div>
            <div className="gs-list-page-tools">
                <Skeleton className="gs-list-page-header-skeleton-search" />
                {showFilter ? <Skeleton className="gs-list-page-header-skeleton-filter" /> : null}
            </div>
        </div>
    );
}

export function FormUrlsTabSkeleton() {
    return (
        <div className="gs-list-page-card gs-form-detail-skeleton" aria-hidden>
            <div className="gs-list-page-card-header">
                <ListPageHeaderSkeleton showFilter />
            </div>
            <div className="gs-form-detail-skeleton-url-placeholder" />
        </div>
    );
}
